import { useCallback, useEffect, useState } from 'react';
import type { Domain, Report, SourceArtefact } from '@zevik/schemas';
import type { Source } from '../api/client';
import {
  advance,
  type AssessmentSnapshot,
  confirmDomains,
  generateReport,
  listArtefacts,
  listAssessments,
  readAssessment,
} from '../api/pipeline';
import { listReports } from '../api/report';
import type { Me } from '../credential/api';
import { apiSentence, sessionLost } from '../credential/session';
import { POLL_INTERVAL_MS, shouldPoll } from '../pipeline/poll';
import { chipFor, type Screen, screenFor } from '../pipeline/screen';
import { Documents } from './Documents';
import { DomainConfirmation } from './DomainConfirmation';
import { Masthead } from './Masthead';
import { Reports } from './Reports';
import { ReportScreen } from './ReportScreen';
import { ReviewScreen } from './ReviewScreen';
import { RunControl } from './RunControl';
import { SignInForm } from './credential/SignInForm';
import { SignInPage } from './credential/SignInPage';

/**
 * One assessment: what it is waiting on, and the screen that serves it
 * (docs/DECISIONS.md ZD-124).
 *
 * ## `next` decides the screen; the page decides nothing from the state
 *
 * The page reads `GET /:id` and hands `next` to `screenFor`. The state is
 * parsed and rendered nowhere — the masthead's chip is the screen's word
 * (`chipFor`), since the first build showed a client the machine's. What a
 * person may do —
 * add a document, press, confirm domains, review — is what `next` says they
 * may do, and a control this page offers is one `next` named.
 *
 * ## Polling is a fact about `next.kind`
 *
 * While `next` is `running` the page re-reads the snapshot every
 * `POLL_INTERVAL_MS`; at any other kind it does not (`pipeline/poll.ts`). A
 * press adopts the `next` in the API's own answer — 202 or either 409 — so
 * the running screen is never shown on the page's assumption that a press
 * worked.
 *
 * ## Documents are read beside the snapshot, and the title from the list
 *
 * `GET /:id/artefacts` is read with the snapshot and again after an upload.
 * The title is the read model's (`GET /v1/assessments`), found by id, because
 * the snapshot is the object's and carries none; one list read per page
 * load, not per poll.
 *
 * ## Reports are read beside them, and shown wherever they exist
 *
 * `GET /:id/reports` is read with the snapshot and again after a report is
 * generated. The panel stands on every screen but the running one where the
 * list is not empty (ZD-125 §5): a report outlives the state that produced
 * it — a revision uploaded afterwards sends the assessment back to the start
 * and the document a client was given is still theirs to read.
 *
 * ## Past the gate
 *
 * Through the gate, `next` names `report_generated` and the screen is the
 * press that generates it; with a report generated, `next` names
 * `actions_exported`, which nothing in the product performs, and the screen
 * says so beside the report (ZD-125 §4). The `unserved` screen no longer
 * claims the acts it declares can be run from the API — that was true of
 * every event it could meet until this one, and false of this one.
 *
 * ## The fixture is the review screen
 *
 * `?fixture` is one assessment at the gate and nothing else, so the page
 * shows the review screen for it without a snapshot: the fixture mirrors the
 * review routes and has no `next` to read. Stated here rather than special-
 * cased silently — every other path through this page goes through `next`.
 *
 * ## Who is signed in, and a session that ends
 *
 * `GET /v1/me` is the record's answer to who is reading. A 401 before
 * anything was read stands a sign-in page in this one's place; a 401 on an
 * act keeps the page mounted with a sign-in above it, and the review
 * screen's draft with it (`credential/session.ts`). A different person
 * signing in remounts the review screen, dropping the draft.
 */
export function AssessmentPage({
  source,
  assessmentId,
  me,
  onSignOut,
  signInFor,
  onSessionLost,
  onSignedIn,
  notice,
  generation,
  sessionRestored,
  reloadToken,
}: {
  source: Source;
  assessmentId: string;
  me: Me | null;
  onSignOut: () => void;
  signInFor: { why: 'load' | 'record'; reason: string } | null;
  onSessionLost: (why: 'load' | 'record', reason: string) => void;
  onSignedIn: () => Promise<void>;
  notice: string | null;
  generation: number;
  sessionRestored: number;
  reloadToken: number;
}): React.JSX.Element {
  const [snapshot, setSnapshot] = useState<AssessmentSnapshot | null>(null);
  const [artefacts, setArtefacts] = useState<SourceArtefact[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [title, setTitle] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pageNotice, setPageNotice] = useState<string | null>(null);
  const fixture = source.base === null;

  const fail = useCallback(
    (error: unknown): void => {
      if (sessionLost(error)) onSessionLost('load', apiSentence(error));
      else setFailure(error instanceof Error ? error.message : String(error));
    },
    [onSessionLost],
  );

  const readSnapshot = useCallback(async (): Promise<AssessmentSnapshot> => {
    const read = await readAssessment(source, assessmentId);
    setSnapshot(read);
    return read;
  }, [source, assessmentId]);

  const readArtefacts = useCallback(async (): Promise<void> => {
    setArtefacts(await listArtefacts(source, assessmentId));
  }, [source, assessmentId]);

  const readReports = useCallback(async (): Promise<void> => {
    setReports(await listReports(source, assessmentId));
  }, [source, assessmentId]);

  // The first read: snapshot, documents, reports and the title, together.
  useEffect(() => {
    if (fixture) return;
    let live = true;
    Promise.all([readSnapshot(), readArtefacts(), readReports(), listAssessments(source)])
      .then(([, , , listed]) => {
        if (!live) return;
        setTitle(listed.find((row) => row.assessment_id === assessmentId)?.title ?? null);
      })
      .catch((error: unknown) => {
        if (live) fail(error);
      });
    return () => {
      live = false;
    };
  }, [fixture, source, assessmentId, readSnapshot, readArtefacts, readReports, fail, reloadToken]);

  /*
   * Polling, exactly while `next` says running. One timeout per snapshot
   * read rather than an interval: each read replaces the snapshot, which
   * re-runs this effect, which arms the next tick only if the new `next` is
   * still running — so a slow read never stacks two, and the first answer
   * that is not `running` is the last read.
   */
  useEffect(() => {
    if (!shouldPoll(snapshot?.next ?? null)) return;
    let live = true;
    const timer = window.setTimeout(() => {
      readSnapshot().catch((error: unknown) => {
        if (live) fail(error);
      });
    }, POLL_INTERVAL_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [snapshot, readSnapshot, fail]);

  const onAdvance = useCallback(async (): Promise<void> => {
    const outcome = await advance(source, assessmentId);
    // Whichever of the three answers: `next` is the API's, adopted as is.
    setSnapshot((current) => (current === null ? current : { ...current, next: outcome.next }));
  }, [source, assessmentId]);

  const onConfirmDomains = useCallback(
    async (domains: Domain[]): Promise<void> => {
      await confirmDomains(source, assessmentId, domains);
      await onAdvance();
    },
    [source, assessmentId, onAdvance],
  );

  const onUploaded = useCallback(
    async (warning: string | null): Promise<void> => {
      setPageNotice(warning);
      await Promise.all([readSnapshot(), readArtefacts()]);
    },
    [readSnapshot, readArtefacts],
  );

  // Through the gate: `next` is re-read, and the screen is what it says.
  const onApproved = useCallback(async (): Promise<void> => {
    await readSnapshot();
  }, [readSnapshot]);

  // A report generated: the list and `next`, both re-read from the API.
  const onGenerate = useCallback(async (): Promise<void> => {
    await generateReport(source, assessmentId);
    await Promise.all([readReports(), readSnapshot()]);
  }, [source, assessmentId, readReports, readSnapshot]);

  const onReportSessionLost = useCallback(
    (reason: string) => {
      onSessionLost('record', reason);
    },
    [onSessionLost],
  );

  if (failure !== null) {
    return (
      <main className="centre">
        <div className="centre__inner">
          <h1>This assessment could not be read</h1>
          <p>{failure}</p>
        </div>
      </main>
    );
  }

  if (signInFor?.why === 'load') {
    return <SignInPage reason={signInFor.reason} onSignedIn={onSignedIn} />;
  }

  if (!fixture && snapshot === null) {
    return (
      <main className="centre">
        <p className="centre__inner">Reading the assessment…</p>
      </main>
    );
  }

  const screen: Screen = fixture ? { kind: 'review', upload: false } : screenFor(snapshot!.next);
  // The screen's word, never the state's (ZD-124 §9): the first build put
  // `canonical model created` in front of a client here.
  const chip = chipFor(screen);

  return (
    <div className="shell">
      <Masthead
        subject={title ?? (fixture ? 'Vetchford (fixture)' : assessmentId)}
        chip={chip.text}
        chipOpen={chip.open}
        me={me}
        onSignOut={onSignOut}
      />

      {notice === null ? null : (
        <p className="shell__notice" role="status">
          {notice}
        </p>
      )}
      {pageNotice === null ? null : (
        <p className="shell__notice" role="status">
          {pageNotice}
        </p>
      )}

      {signInFor?.why === 'record' ? (
        <section className="reauth" aria-label="Sign in again">
          <div className="reauth__inner">
            <h2>Your session has ended</h2>
            <p>
              Sign in to carry on. Every decision you already recorded is kept, and the one you were
              writing is still below.
            </p>
            <p className="reauth__reason">{signInFor.reason}</p>
            <SignInForm onSignedIn={onSignedIn} compact />
          </div>
        </section>
      ) : null}

      {screen.kind === 'review' ? (
        <ReviewScreen
          key={generation}
          source={source}
          assessmentId={assessmentId}
          sessionRestored={sessionRestored}
          reloadToken={reloadToken}
          onSessionLost={onSessionLost}
          onApproved={onApproved}
        />
      ) : (
        <main className="page">
          {screen.kind === 'domains' ? (
            <DomainConfirmation enabled={snapshot!.enabled_domains} onConfirm={onConfirmDomains} />
          ) : null}
          {screen.kind === 'ready' || screen.kind === 'running' || screen.kind === 'stopped' ? (
            <RunControl screen={screen} onAdvance={onAdvance} />
          ) : null}
          {screen.kind === 'report' ? <ReportScreen onGenerate={onGenerate} /> : null}
          {screen.kind === 'reported' ? (
            <section className="run" aria-label="Report generated">
              <h2>The report is ready</h2>
              <p className="run__note">
                It is below, to read here or download. What would follow it — an action register
                drawn from the findings — is not built yet, so this is as far as the assessment goes
                for now.
              </p>
            </section>
          ) : null}
          {screen.kind === 'unserved' ? (
            <section className="run" aria-label="Waiting on something this console cannot do">
              <h2>Waiting on something this console cannot do yet</h2>
              <p className="run__note">
                {screen.events.length === 0
                  ? 'The assessment is not waiting on anything a person can do here.'
                  : 'What comes next is not on a screen in this console yet.'}
              </p>
            </section>
          ) : null}
          {screen.kind === 'running' ? null : (
            <Reports source={source} reports={reports} onSessionLost={onReportSessionLost} />
          )}
          {screen.kind === 'running' ? null : (
            <Documents
              source={source}
              assessmentId={assessmentId}
              artefacts={artefacts}
              upload={screen.kind === 'documents' || screen.upload}
              onUploaded={onUploaded}
              onSessionLost={(reason) => {
                onSessionLost('record', reason);
              }}
            />
          )}
        </main>
      )}
    </div>
  );
}
